import type { Prototype } from '@atlas/api-contract';
import { Loader2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

import { toDisplayableError } from '@/components/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useDeletePrototype } from './prototypes-queries';

export interface DeletePrototypeDialogProps {
  /** The prototype queued for deletion, or `null` when none is. */
  prototype: Prototype | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Called once the delete has succeeded, before the dialog closes.
   *
   * Optional because most callers are lists that only need the cache
   * invalidation the mutation already performs. The viewer needs more than
   * that: it is *displaying* the prototype that just stopped existing, and has
   * to navigate away rather than re-render itself into a 404.
   */
  onDeleted?: (prototype: Prototype) => void;
}

/**
 * Delete a prototype and everything uploaded to it.
 *
 * The delete cascades through every version, every file those versions put in
 * object storage, and every comment thread and reply left on them — work by
 * people who are not in the room — and there is no undo and no soft delete. So
 * the confirmation is the prototype's own name, typed out: it cannot be
 * satisfied by muscle memory, and typing it means reading it.
 *
 * The consequences are spelled out in full rather than summarised as
 * "permanent", because "permanent" does not tell anyone that a colleague's
 * unresolved thread goes with it.
 */
export const DeletePrototypeDialog = ({
  prototype,
  onOpenChange,
  onDeleted,
}: DeletePrototypeDialogProps) => {
  const deletePrototype = useDeletePrototype();
  const confirmationId = useId();
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const open = prototype !== null;

  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError(null);
    }
  }, [open]);

  const canDelete =
    prototype !== null &&
    confirmation.trim() === prototype.name &&
    !deletePrototype.isPending;

  const handleDelete = async () => {
    if (prototype === null) {
      return;
    }

    setError(null);

    try {
      await deletePrototype.mutateAsync({
        prototypeId: prototype.id,
        projectId: prototype.projectId,
      });

      toast.success(`Deleted “${prototype.name}”.`);
      onDeleted?.(prototype);
      onOpenChange(false);
    } catch (caught: unknown) {
      setError(toDisplayableError(caught));
    }
  };

  const versionCount = prototype?.latestVersionNumber ?? 0;
  const openThreads = prototype?.openThreadCount ?? 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && deletePrototype.isPending) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete “{prototype?.name ?? ''}”?</DialogTitle>
          <DialogDescription>
            This deletes the prototype and everything uploaded to it: every
            version, every file those versions put in storage, and every comment
            thread and reply left on them, by anyone. It cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {prototype === null ? null : (
          <div className="space-y-2">
            <Label htmlFor={confirmationId}>
              Type{' '}
              <span className="font-semibold text-foreground">{prototype.name}</span>{' '}
              to confirm
            </Label>
            <Input
              id={confirmationId}
              autoComplete="off"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
              placeholder={prototype.name}
            />
            <p className="text-xs text-muted-foreground">
              {versionCount === 0
                ? 'Nothing has been uploaded to this prototype yet.'
                : `Every uploaded version up to v${String(versionCount)}, and every file in ${versionCount === 1 ? 'it' : 'them'}, will be destroyed.`}
              {openThreads > 0
                ? ` ${String(openThreads)} unresolved comment ${openThreads === 1 ? 'thread goes' : 'threads go'} with them.`
                : ''}
            </p>
          </div>
        )}

        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={deletePrototype.isPending}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canDelete}
            onClick={() => {
              void handleDelete();
            }}
          >
            {deletePrototype.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="animate-spin" />
                Deleting…
              </>
            ) : (
              'Delete prototype'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
